import {BehaviorSubject, Observable} from 'rxjs';
import {Injectable} from "@angular/core";
import {Guid} from "guid-typescript";
import {Success, SuccessList} from "@portal/api/responses/api-response";
import {ApiBaseService} from "@portal/api/api-base-service";
import {SearchRequest} from "@portal/api/requests/search-request";
import {parseObjectToQueryString} from "@portal/shared/utils";
import {ListService} from "@portal/api/base/list-service";
import {AddActivityBarTransaction} from "@portal/api/contracts/activity-bar/add-activity-bar-transaction";
import {ActivityBarTransaction} from "@portal/api/contracts/activity-bar/activity-bar-transaction";
import {SearchRequestFilter} from "@portal/api/requests/search-request-filter";
import {StorageService} from "@portal/shared/services/storage.service";
import {PartySelectedService} from "@portal/shared/services/party-selected.service";
import {OrganizationType} from "@portal/api/enums/organization-type";
import {HttpClient} from "@angular/common/http";

interface ModalCalculator {
    open: boolean;
    eventId: Guid;
    barId?: Guid;
}

@Injectable({
    providedIn: 'root'
})
export class ActivityBarService extends ApiBaseService implements ListService {
    private modal = new BehaviorSubject<Partial<ModalCalculator>>({open: false});

    constructor(
        public http: HttpClient,
        private _storageService: StorageService,
        private _partySelectedService: PartySelectedService) {
        super(http);
    }

    connect(): Observable<Partial<ModalCalculator>> {
        return this.modal.asObservable();
    }

    modalSet(config: Partial<ModalCalculator>) {
        this.modal.next({...config});
    }

    addTransaction(obj: AddActivityBarTransaction): Observable<Success<ActivityBarTransaction>> {
        return this.post<Success<ActivityBarTransaction>, AddActivityBarTransaction>(obj, this.getBaseRoute());
    }

    list<ActivityBarTransactionList>(obj: SearchRequest): Observable<SuccessList<ActivityBarTransactionList>> {
        return this.get<SuccessList<ActivityBarTransactionList>>(this.getBaseRoute() + `?${parseObjectToQueryString(obj)}`);
    }

    getAll<ActivityBarTransactionList>(activityId: Guid): Observable<SuccessList<ActivityBarTransactionList>> {
        let request = {
            page: 0,
            pageSize: 1,
            sortDirection: 0,
            sortField: null,
            getAll: true
        } as SearchRequest;
        return this.get<SuccessList<ActivityBarTransactionList>>(this.getBaseRoute() + `?activityId=${activityId}&${parseObjectToQueryString(request)}`);
    }

    getAllByBar<ActivityBarTransactionList>(activityId: Guid, barId: Guid): Observable<SuccessList<ActivityBarTransactionList>> {
        let request = {
            page: 0,
            pageSize: 1,
            sortDirection: 0,
            sortField: null,
            getAll: true
        } as SearchRequest;
        return this.get<SuccessList<ActivityBarTransactionList>>(this.getBaseRoute() + `?activityId=${activityId}&barId=${barId}&${parseObjectToQueryString(request)}`);
    }

    deleteTransaction(id: Guid): Observable<boolean> {
        return this.deleteBase<boolean>(`${this.getBaseRoute()}/${id.toString()}`);
    }

    getTotalRegistered(activityId: Guid): Observable<number> {
        return this.get<number>(`${this.getBaseRoute()}/total-registered/${activityId.toString()}`);
    }

    getTotalRegisteredByBar(barId: Guid): Observable<number> {
        return this.get<number>(`${this.getBaseRoute()}/total-registered-by-bar/${barId.toString()}`);
    }

    private getBaseRoute(): string {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return `v1/orgs/${orgId}/events/bar-transactions`;
        }
        return `v1/orgs/${orgId}/parties/${partyId}/events/bar-transactions`;
    }

    private getOrgId(): Guid {
        return this._storageService.getOrg()?.id;
    }

    private getPartyId(): Guid {
        return this._partySelectedService.getPartyId() ?? this._storageService.getParty()?.id;
    }

    private isOrgScope(): boolean {
        return this._storageService.getOrg()?.type === OrganizationType.Association;
    }
}
